// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/PatientRequestDetails/PatientRequestDetailsPage.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "../NewPatientRequests/NewPatientRequestsPage.css";
import "../../FamilyDoctor/PatientRequestDetails/PatientRequestDetailsPage.css";
import "./PatientRequestDetailsPage.css";

import PatientRequestActivityPanel from "../../components/PatientRequestActivityPanel";
import PatientRequestAttachmentOutcomeCard, {
  AttachmentOutcomeAction,
} from "../../components/PatientRequestAttachmentOutcomeCard";

import {
  ApiError,
  clinicAdminApi,
  ClinicAdminPatientRequestDetailsResponse,
  ClinicAdminPatientRequestListItem,
} from "../../../../api/clinicAdmin";
import {
  FinalizeEmailPreview,
  previewFamilyDoctorRequestFinalize,
} from "../../../../api/requests";
import {
  formatDateOnly,
  formatDateTime,
} from "../../../../utils/clinicAdminHelpers";

type UiTag = {
  key: string;
  label: string;
  title?: string;
  className?: string;
};

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function asLower(value: unknown): string {
  return asText(value).toLowerCase();
}

function normalizeStatus(status: unknown): string {
  const raw = asText(status);
  if (!raw) return "";

  const key = raw.toLowerCase();

  if (key === "in_review") return "InReview";
  if (key === "notaccepted") return "Declined";

  if (key === "attachment_complete" || key === "attachmentcomplete") {
    return "AttachmentComplete";
  }

  if (key === "closedpatientwithdrew") return "ClosedPatientWithdrew";
  if (key === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (key === "closednoshow") return "ClosedNoShow";

  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "ClosedClinicDeclinedAfterMeetAndGreet";
  }

  return raw;
}

function statusDisplayLabel(status: unknown): string {
  const normalized = normalizeStatus(status);
  const key = normalized.toLowerCase();

  if (key === "submitted") return "Submitted";
  if (key === "inreview") return "Under Review";
  if (key === "waitlist") return "Waitlisted";
  if (key === "accepted") return "Invited for Meet & Greet";
  if (key === "attachmentcomplete") return "Attachment Complete";
  if (key === "declined") return "Declined by Clinic";
  if (key === "closedpatientwithdrew") return "Closed — Patient Withdrew";
  if (key === "closedacceptedelsewhere") return "Closed — Accepted Elsewhere";
  if (key === "closednoshow") return "Closed — Meet & Greet Not Completed";

  if (key === "closedclinicdeclinedaftermeetandgreet") {
    return "Closed — Clinic Declined After Meet & Greet";
  }

  if (key === "cancelled") return "Cancelled by Patient";
  if (key === "expired") return "Auto-Expired";

  return normalized || "Unknown";
}

function statusPillClass(status: unknown): string {
  const key = normalizeStatus(status).toLowerCase();

  if (key === "submitted" || key === "inreview") {
    return "ocf-status ocf-status--pending";
  }

  if (key === "accepted") return "ocf-status ocf-status--accepted";
  if (key === "waitlist") return "ocf-status ocf-status--waitlist";

  if (
    key === "declined" ||
    key === "cancelled" ||
    key === "expired" ||
    key === "closedclinicdeclinedaftermeetandgreet"
  ) {
    return "ocf-status ocf-status--declined";
  }

  if (
    key === "attachmentcomplete" ||
    key === "closedpatientwithdrew" ||
    key === "closedacceptedelsewhere" ||
    key === "closednoshow"
  ) {
    return "ocf-status ocf-status--closed";
  }

  return "ocf-status";
}

function patientDisplayName(patient: any): string {
  if (!patient) return "Unnamed patient";

  const first = asText(patient.firstName);
  const last = asText(patient.lastName);
  const composed = [first, last].filter(Boolean).join(" ").trim();

  if (composed) return composed;

  const legacy = asText(patient.fullName || patient.name);
  return legacy || "Unnamed patient";
}

function formatRequestFor(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const key = raw.toLowerCase();

  if (key === "myself") return "Myself";
  if (key === "mychild" || key === "minor" || key === "minorchild") {
    return "My Minor Child";
  }
  if (key === "myfamily" || key === "family" || key === "adultfamilymember") {
    return "My Family";
  }

  return raw.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function isNavigatorSourceType(value: unknown): boolean {
  const sourceType = asText(value);

  return (
    sourceType === "NavigatorPublicRequest" ||
    sourceType === "NavigatorUpload" ||
    sourceType === "OHT" ||
    sourceType === "FutureHCC" ||
    sourceType === "ManualNavigatorEntry"
  );
}

function getNavigatorContext(
  request: ClinicAdminPatientRequestListItem | null
): any | null {
  if (!request) return null;

  const anyRequest = request as any;
  const navigator = anyRequest.navigator;

  if (navigator?.isNavigatorAssisted) return navigator;

  if (
    anyRequest.navigatorCaseId !== null &&
    anyRequest.navigatorCaseId !== undefined
  ) {
    return {
      isNavigatorAssisted: true,
      navigatorCaseId: anyRequest.navigatorCaseId,
      sourceType: anyRequest.sourceType ?? null,
      sourceLabel: anyRequest.sourceLabel || "Community Navigator",
      priorityLevel:
        anyRequest.navigatorPriorityLevel || anyRequest.priorityLevel || null,
      forwardedByNavigatorUserId:
        anyRequest.forwardedByNavigatorUserId ?? null,
      forwardedByNavigatorName: anyRequest.forwardedByNavigatorName ?? null,
      forwardedAt: anyRequest.forwardedAt ?? null,
      publicSummary: anyRequest.navigatorSummary || null,
    };
  }

  if (
    isNavigatorSourceType(anyRequest.sourceType) ||
    anyRequest.navigatorSummary ||
    anyRequest.navigatorPriorityLevel ||
    anyRequest.forwardedByNavigatorUserId ||
    anyRequest.forwardedAt
  ) {
    return {
      isNavigatorAssisted: true,
      navigatorCaseId: anyRequest.navigatorCaseId ?? null,
      sourceType: anyRequest.sourceType ?? null,
      sourceLabel: anyRequest.sourceLabel || "Community Navigator",
      priorityLevel:
        anyRequest.navigatorPriorityLevel || anyRequest.priorityLevel || null,
      forwardedByNavigatorUserId:
        anyRequest.forwardedByNavigatorUserId ?? null,
      forwardedByNavigatorName: anyRequest.forwardedByNavigatorName ?? null,
      forwardedAt: anyRequest.forwardedAt ?? null,
      publicSummary: anyRequest.navigatorSummary || null,
    };
  }

  return null;
}

function getRequestSourceLabel(
  request: ClinicAdminPatientRequestListItem | null
): string {
  const navigator = getNavigatorContext(request);
  if (navigator) return asText(navigator.sourceLabel) || "Community Navigator";

  return "Public";
}

function normalizePriorityLabel(value: unknown): string | null {
  const raw = asText(value);
  if (!raw) return null;

  const compact = raw.toLowerCase().replace(/[\s_-]+/g, "");

  if (compact === "urgent" || compact.includes("priorityurgent")) {
    return "Urgent";
  }

  if (compact === "high" || compact.includes("priorityhigh")) {
    return "High";
  }

  if (
    compact === "medium" ||
    compact === "moderate" ||
    compact.includes("prioritymedium") ||
    compact.includes("prioritymoderate")
  ) {
    return "Medium";
  }

  if (compact === "low" || compact.includes("prioritylow")) {
    return "Low";
  }

  if (
    compact === "routine" ||
    compact === "standard" ||
    compact.includes("priorityroutine") ||
    compact.includes("prioritystandard")
  ) {
    return "Routine";
  }

  return null;
}

function priorityPillClass(priority: string): string {
  const key = priority.toLowerCase();

  if (key === "urgent") return "ocf-priority-pill ocf-priority-pill--urgent";
  if (key === "high") return "ocf-priority-pill ocf-priority-pill--high";
  if (key === "medium") return "ocf-priority-pill ocf-priority-pill--medium";
  if (key === "low") return "ocf-priority-pill ocf-priority-pill--low";

  return "ocf-priority-pill ocf-priority-pill--routine";
}

function getPriorityLabel(
  request: ClinicAdminPatientRequestListItem | null
): string {
  if (!request) return "Routine";

  const directCandidates = [
    (request as any).priorityLevel,
    (request as any).PriorityLevel,
    (request as any).priority,
    (request as any).Priority,
    (request as any).request?.priorityLevel,
    (request as any).request?.PriorityLevel,
    (request as any).request?.priority,
    (request as any).request?.Priority,
  ];

  for (const candidate of directCandidates) {
    const normalized = normalizePriorityLabel(candidate);
    if (normalized) return normalized;
  }

  const tags = Array.isArray((request as any).priorityTags)
    ? (request as any).priorityTags
    : [];

  for (const tag of tags) {
    const normalized =
      normalizePriorityLabel(tag?.key) ||
      normalizePriorityLabel(tag?.label) ||
      normalizePriorityLabel(tag?.value);

    if (normalized) return normalized;
  }

  return "Routine";
}

function getServerTag(
  request: ClinicAdminPatientRequestListItem | null,
  keys: string[]
): any | null {
  if (!request) return null;

  const wanted = new Set(keys.map((k) => k.toLowerCase()));
  const tags = Array.isArray((request as any).priorityTags)
    ? (request as any).priorityTags
    : [];

  for (const tag of tags) {
    const tagKey = asLower(tag?.key);
    const tagLabel = asLower(tag?.label);

    if (wanted.has(tagKey) || wanted.has(tagLabel)) return tag;
  }

  return null;
}

function patientBaselineLabel(
  request: ClinicAdminPatientRequestListItem | null
): string {
  if (!request) return "Unknown";

  const baseline = asLower((request as any).patientStatusBeforeRequest);

  if (baseline === "unattached") return "Unattached";
  if (baseline === "attached") return "Attached / Switching";

  if (
    getServerTag(request, ["unattached_moh", "unattached", "MOH unattached"])
  ) {
    return "Unattached";
  }

  if (getServerTag(request, ["attached_moh", "attached"])) {
    return "Attached / Switching";
  }

  return "Unknown";
}

function patientBaselineClass(
  request: ClinicAdminPatientRequestListItem | null
): string {
  const label = patientBaselineLabel(request);

  if (label === "Unattached") {
    return "ocf-baseline-pill ocf-baseline-pill--unattached";
  }

  if (label === "Attached / Switching") {
    return "ocf-baseline-pill ocf-baseline-pill--attached";
  }

  return "ocf-baseline-pill ocf-baseline-pill--unknown";
}

function buildTopTags(
  request: ClinicAdminPatientRequestListItem | null
): UiTag[] {
  if (!request) return [];

  const tags: UiTag[] = [];

  tags.push({
    key: "baseline",
    label: patientBaselineLabel(request),
    title: "Patient status before this request, based on the intake reason.",
    className: patientBaselineClass(request),
  });

  const sameFsaTag = getServerTag(request, [
    "within_catchment",
    "same_fsa",
    "samefsa",
  ]);
  if (sameFsaTag) {
    tags.push({
      key: "same_fsa",
      label: "Same FSA",
      title:
        asText(sameFsaTag.basis || sameFsaTag.Basis) ||
        "Patient postal code appears to be in the same FSA as the clinic.",
      className: "ocf-detail-tag ocf-detail-tag--fsa",
    });
  }

  const newbornTag = getServerTag(request, ["newborn"]);
  if (newbornTag) {
    tags.push({
      key: "newborn",
      label: "Newborn",
      title:
        asText(newbornTag.basis || newbornTag.Basis) ||
        "Patient age appears to be under 1 year.",
      className: "ocf-detail-tag ocf-detail-tag--newborn",
    });
  }

  const familyMemberTag = getServerTag(request, [
    "family_member_existing_patient",
    "family_member",
  ]);
  if (familyMemberTag) {
    tags.push({
      key: "family_member",
      label: "Family member",
      title:
        asText(familyMemberTag.basis || familyMemberTag.Basis) ||
        "Family member of an existing patient, if confirmed by the clinic.",
      className: "ocf-detail-tag",
    });
  }

  const priority = getPriorityLabel(request);
  const navigator = getNavigatorContext(request);

  tags.push({
    key: "priority",
    label: `${priority} priority`,
    title: navigator
      ? "Navigator priority level carried with this forwarded request."
      : "Public/direct requests default to Routine priority unless system tags indicate otherwise.",
    className: priorityPillClass(priority),
  });

  tags.push({
    key: "source",
    label: navigator ? "Navigator" : "Public",
    title: navigator
      ? asText(navigator.sourceLabel) || "Navigator-forwarded request."
      : "Public patient request.",
    className: navigator
      ? "ocf-source-pill ocf-source-pill--navigator"
      : "ocf-source-pill ocf-source-pill--public",
  });

  return tags;
}

function isFinalized(
  request: ClinicAdminPatientRequestListItem | null
): boolean {
  if (!request) return false;

  const statusKey = normalizeStatus(request.statusName || "").toLowerCase();

  return (
    statusKey === "attachmentcomplete" ||
    statusKey === "declined" ||
    statusKey === "cancelled" ||
    statusKey === "expired" ||
    statusKey === "closedpatientwithdrew" ||
    statusKey === "closedacceptedelsewhere" ||
    statusKey === "closednoshow" ||
    statusKey === "closedclinicdeclinedaftermeetandgreet"
  );
}

function finalizeSavedMessage(
  action: AttachmentOutcomeAction,
  fallback?: string | null
): string {
  if (fallback) return fallback;

  if (action === "AttachmentComplete") return "Attachment marked complete.";
  if (action === "PatientWithdrew") {
    return "Request closed: patient withdrew or declined.";
  }
  if (action === "PatientAcceptedElsewhere") {
    return "Request closed: patient accepted elsewhere.";
  }
  if (action === "NoShow") {
    return "Request closed: meet and greet was not completed.";
  }
  if (action === "ClinicDeclinedAfterMeetAndGreet") {
    return "Request closed: clinic declined after meet and greet.";
  }
  if (action === "MovedToWaitlist") return "Request moved to waitlist.";

  return "Request updated.";
}

function finalizeActionRequiresEmailPreview(
  action: AttachmentOutcomeAction
): boolean {
  return action === "MovedToWaitlist";
}

function finalizeActionLabel(action: AttachmentOutcomeAction): string {
  if (action === "MovedToWaitlist") return "Move back to waitlist";
  if (action === "AttachmentComplete") return "Attachment Complete";
  if (action === "PatientWithdrew") return "Patient withdrew / declined";
  if (action === "PatientAcceptedElsewhere") return "Patient accepted elsewhere";
  if (action === "NoShow") return "Meet & Greet was not completed";
  if (action === "ClinicDeclinedAfterMeetAndGreet") {
    return "Clinic declined after Meet & Greet";
  }

  return "Finalize request";
}

function finalizeConfirmLabel(action: AttachmentOutcomeAction): string {
  if (action === "MovedToWaitlist") {
    return "Confirm Move to Waitlist & Send Email";
  }

  return "Confirm Outcome";
}

function KeyValue({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  const safeValue =
    value === null || value === undefined || value === "" ? "—" : value;

  return (
    <div className="ocf-kv-item">
      <span className="k">{label}</span>
      <span className="v">{safeValue}</span>
    </div>
  );
}

export default function PatientRequestDetailsPage() {
  const navigate = useNavigate();
  const params = useParams();

  const requestId = useMemo(() => {
    const n = Number(params.requestId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params.requestId]);

  const [loading, setLoading] = useState(true);
  const [details, setDetails] =
    useState<ClinicAdminPatientRequestDetailsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submittingFinalize, setSubmittingFinalize] =
    useState<AttachmentOutcomeAction | null>(null);
  const [previewLoadingFinalize, setPreviewLoadingFinalize] =
    useState<AttachmentOutcomeAction | null>(null);
  const [pendingFinalizeAction, setPendingFinalizeAction] =
    useState<AttachmentOutcomeAction | null>(null);
  const [pendingFinalizeNote, setPendingFinalizeNote] = useState<string | null>(
    null
  );
  const [finalizePreview, setFinalizePreview] =
    useState<FinalizeEmailPreview | null>(null);

  const request = details?.request || null;

  const loadDetails = useCallback(async () => {
    if (!requestId) {
      setError("Invalid request ID.");
      setDetails(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await clinicAdminApi.getPatientRequestDetails(requestId);
      setDetails(data);
    } catch (e: any) {
      setError(
        e instanceof ApiError ? e.message : "Failed to load request details"
      );
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  function closeFinalizePreview() {
    if (submittingFinalize) return;
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setFinalizePreview(null);
  }

  async function onRequestFinalizePreview(
    action: AttachmentOutcomeAction,
    note?: string | null
  ) {
    if (!finalizeActionRequiresEmailPreview(action)) {
      await onFinalize(action, note);
      return;
    }

    if (!request?.requestId) return;

    setPreviewLoadingFinalize(action);
    setError(null);
    setSuccessMessage(null);
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setFinalizePreview(null);

    try {
      const preview = await previewFamilyDoctorRequestFinalize(
        request.requestId,
        action as any,
        note ?? null
      );
      setPendingFinalizeAction(action);
      setPendingFinalizeNote(note ?? null);
      setFinalizePreview(preview);
    } catch (e: any) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Failed to prepare the patient email preview"
      );
    } finally {
      setPreviewLoadingFinalize(null);
    }
  }

  async function onConfirmFinalizePreview() {
    if (!request?.requestId || !pendingFinalizeAction) return;

    await onFinalize(pendingFinalizeAction, pendingFinalizeNote ?? null);
    setPendingFinalizeAction(null);
    setPendingFinalizeNote(null);
    setFinalizePreview(null);
  }

  async function onFinalize(
    action: AttachmentOutcomeAction,
    note?: string | null
  ) {
    if (!request?.requestId) return;

    setSubmittingFinalize(action);
    setError(null);
    setSuccessMessage(null);

    try {
      let response: any = null;

      if (action === "AttachmentComplete") {
        response = await clinicAdminApi.attachmentComplete(request.requestId, {
          note: note ?? null,
        });
      } else {
        response = await clinicAdminApi.declinePostAcceptance(
          request.requestId,
          {
            action: action as any,
            note: note ?? null,
          }
        );
      }

      setSuccessMessage(finalizeSavedMessage(action, response?.message));
      setPendingFinalizeAction(null);
      setPendingFinalizeNote(null);
      setFinalizePreview(null);
      await loadDetails();
    } catch (e: any) {
      setError(e instanceof ApiError ? e.message : "Failed to finalize request");
    } finally {
      setSubmittingFinalize(null);
    }
  }

  const patientName = useMemo(
    () => patientDisplayName(request?.patient),
    [request?.patient]
  );

  const normalizedStatus = useMemo(
    () => normalizeStatus(request?.statusName || ""),
    [request?.statusName]
  );

  const statusKey = normalizedStatus.toLowerCase();

  const topTags = useMemo(() => buildTopTags(request), [request]);

  const finalized = useMemo(() => isFinalized(request), [request]);

  const canShowAttachmentOutcome =
    !!request && !finalized && statusKey === "accepted";

  const finalizeBusy = !!submittingFinalize || !!previewLoadingFinalize;

  const activities = useMemo(() => {
    const r = request as any;

    if (Array.isArray(r?.activities)) return r.activities;
    if (Array.isArray(r?.timeline?.activities)) return r.timeline.activities;

    return [];
  }, [request]);

  const statusHistory = useMemo(() => {
    const r = request as any;

    if (Array.isArray(r?.statusHistory)) return r.statusHistory;
    if (Array.isArray(r?.timeline?.statusHistory)) {
      return r.timeline.statusHistory;
    }

    return [];
  }, [request]);

  const requestForLabel = formatRequestFor(request?.requestFor);

  const navigatorContext = useMemo(
    () => getNavigatorContext(request),
    [request]
  );

  const requestSourceLabel = useMemo(
    () => getRequestSourceLabel(request),
    [request]
  );

  const coverageLabel = useMemo(() => {
    const r = request as any;
    const p = request?.patient as any;

    return (
      asText(p?.coverageTypeName) ||
      asText(p?.coverageName) ||
      asText(p?.coverage) ||
      asText(p?.coverageType) ||
      asText(r?.coverageTypeName) ||
      asText(r?.coverageName) ||
      asText(r?.coverage) ||
      asText(r?.coverageType) ||
      "—"
    );
  }, [request]);

  return (
    <div className="ocf-request-detail-page ca-request-detail-page">
      <div className="ocf-detail-top-actions">
        <button
          className="ocf-btn ocf-btn--secondary"
          type="button"
          onClick={() => navigate("/portal/clinic-admin/patient-requests")}
        >
          ← Back to requests
        </button>

        <button
          className="ocf-btn ocf-btn--secondary"
          type="button"
          onClick={loadDetails}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="ocf-card ocf-detail-empty" role="status">
          <span className="ocf-detail-empty-title">
            Loading request details…
          </span>
        </div>
      ) : error && !request ? (
        <div className="ocf-card">
          <div className="ocf-error">{error}</div>
        </div>
      ) : !request ? (
        <div className="ocf-card ocf-detail-empty">
          <span className="ocf-detail-empty-title">
            No request details found.
          </span>
        </div>
      ) : (
        <div className="ocf-request-detail-shell">
          <section className="ocf-request-detail-main">
            <div className="ocf-card ocf-request-hero">
              <div className="ocf-detail-header ocf-detail-header--stacked">
                <div className="ocf-detail-header-main">
                  <div className="ocf-page-eyebrow">
                    Patient request details
                  </div>

                  <div className="ocf-detail-title-row">
                    <h1 className="ocf-detail-title">{patientName}</h1>
                    <span className={statusPillClass(normalizedStatus)}>
                      {statusDisplayLabel(request.statusName)}
                    </span>
                  </div>

                  <p className="ocf-detail-patient">
                    Request #{request.requestId}
                    {request.familyDoctorName
                      ? ` · ${request.familyDoctorName}`
                      : ""}
                  </p>

                  <div className="ocf-detail-tag-row" aria-label="Request tags">
                    {topTags.map((tag) => (
                      <span
                        key={tag.key}
                        className={tag.className || "ocf-detail-tag"}
                        title={tag.title}
                      >
                        {tag.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {error ? (
              <div className="ocf-error" role="alert">
                {error}
              </div>
            ) : null}

            {successMessage ? (
              <div className="ocf-notice ocf-notice--success" role="status">
                {successMessage}
              </div>
            ) : null}

            {statusKey === "closedacceptedelsewhere" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the patient accepted elsewhere.
              </div>
            ) : null}
            {statusKey === "closedpatientwithdrew" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the patient withdrew or declined
                to proceed.
              </div>
            ) : null}
            {statusKey === "closednoshow" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the Meet &amp; Greet was not
                completed.
              </div>
            ) : null}
            {statusKey === "closedclinicdeclinedaftermeetandgreet" ? (
              <div className="ocf-notice ocf-notice--info">
                This request was closed because the clinic declined after the
                Meet &amp; Greet stage.
              </div>
            ) : null}
            {statusKey === "attachmentcomplete" ? (
              <div className="ocf-notice ocf-notice--info">
                Attachment is complete. This request is closed.
              </div>
            ) : null}

            {canShowAttachmentOutcome ? (
              <section
                className="ocf-request-action-panel ocf-request-action-panel--finalize"
                aria-label="Post-invitation outcome actions"
              >
                <div className="ocf-request-action-panel-copy">
                  <div className="ocf-request-action-panel-eyebrow">
                    Meet &amp; Greet outcome
                  </div>
                  <p>
                    Complete or close this request after the invitation stage.
                  </p>
                </div>

                <div className="ocf-request-action-panel-body">
                  <PatientRequestAttachmentOutcomeCard
                    currentStatus={normalizedStatus}
                    disabled={finalizeBusy}
                    submittingAction={
                      (submittingFinalize || previewLoadingFinalize) as any
                    }
                    onFinalize={(action, note) =>
                      onRequestFinalizePreview(
                        action as AttachmentOutcomeAction,
                        note
                      )
                    }
                  />
                </div>
              </section>
            ) : null}

            <section className="ocf-section ocf-request-overview-card">
              <div className="ocf-request-overview-header">
                <div>
                  <h3 className="ocf-section-title">Request Overview</h3>
                  <p className="ocf-request-overview-subtitle">
                    Patient, applicant, request details, and navigator context
                    in one readable workspace.
                  </p>
                </div>
              </div>

              <div className="ocf-request-overview-stack">
                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Patient Snapshot
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue label="Name" value={patientName} />
                    <KeyValue label="Gender" value={request.patient?.gender} />
                    <KeyValue
                      label="DOB"
                      value={formatDateOnly(request.patient?.dateOfBirth)}
                    />
                    <KeyValue
                      label="Postal code"
                      value={request.patient?.postalCode}
                    />
                    <KeyValue label="Request for" value={requestForLabel} />
                    <KeyValue label="Coverage" value={coverageLabel} />
                  </div>
                  <p className="ocf-section-note">
                    Applicant contact is shown separately because the applicant
                    may not be the patient.
                  </p>
                </section>

                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Applicant Contact
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue
                      label="Email"
                      value={
                        request.patient?.applicantEmail ||
                        request.patient?.email
                      }
                    />
                    <KeyValue
                      label="Phone"
                      value={
                        request.patient?.applicantPhone ||
                        request.patient?.phoneNumber
                      }
                    />
                  </div>
                </section>

                <section className="ocf-request-overview-group">
                  <div className="ocf-request-overview-group-header">
                    Request Details
                  </div>
                  <div className="ocf-kv ocf-request-overview-kv">
                    <KeyValue label="Doctor" value={request.familyDoctorName} />
                    <KeyValue
                      label="Doctor CPSO"
                      value={request.familyDoctorCpso}
                    />
                    <KeyValue
                      label="Current status"
                      value={statusDisplayLabel(request.statusName)}
                    />
                    <KeyValue label="Source" value={requestSourceLabel} />
                    <KeyValue
                      label="Priority"
                      value={getPriorityLabel(request)}
                    />
                    <KeyValue
                      label="Submitted"
                      value={formatDateTime(request.submittedAt)}
                    />
                  </div>

                  <div className="ocf-reasons-block">
                    <div className="k">MOH reason(s)</div>
                    {(request.reasons || []).length === 0 ? (
                      <div className="ocf-muted">—</div>
                    ) : (
                      <ul className="ocf-reasons">
                        {(request.reasons || []).map((reason) => (
                          <li key={reason.id}>{reason.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {request.otherReasonText ? (
                    <div className="ocf-reasons-block">
                      <div className="k">Other non-medical reason</div>
                      <div className="ocf-note">{request.otherReasonText}</div>
                    </div>
                  ) : null}
                </section>

                {navigatorContext ? (
                  <section className="ocf-request-overview-group ocf-request-overview-group--navigator">
                    <div className="ocf-request-overview-group-header">
                      Navigator Context
                    </div>
                    <div className="ocf-kv ocf-request-overview-kv">
                      <KeyValue
                        label="Source"
                        value={
                          asText(navigatorContext.sourceLabel) ||
                          "Community Navigator"
                        }
                      />
                      <KeyValue
                        label="Priority"
                        value={getPriorityLabel(request)}
                      />
                      <KeyValue
                        label="Forwarded by"
                        value={navigatorContext.forwardedByNavigatorName}
                      />
                      <KeyValue
                        label="Forwarded"
                        value={formatDateTime(navigatorContext.forwardedAt)}
                      />
                    </div>

                    <div className="ocf-reasons-block">
                      <div className="k">Navigator summary</div>
                      <div className="ocf-note">
                        {navigatorContext.publicSummary ||
                          "This request was forwarded through the Navigator workflow for clinic review."}
                      </div>
                    </div>
                  </section>
                ) : null}
              </div>
            </section>

            <details className="ocf-section ocf-status-timeline-details">
              <summary className="ocf-status-timeline-summary">
                <span className="ocf-section-title ocf-status-timeline-title">
                  Status Timeline
                </span>
                <span className="ocf-status-timeline-hint">
                  Click to expand
                </span>
              </summary>
              <div className="ocf-kv ocf-status-timeline-body">
                <KeyValue
                  label="Created"
                  value={formatDateTime(request.createdAt)}
                />
                <KeyValue
                  label="Submitted"
                  value={formatDateTime(request.submittedAt)}
                />
                <KeyValue
                  label="Verified"
                  value={formatDateTime(request.verifiedAt)}
                />
                <KeyValue
                  label="Decision"
                  value={formatDateTime(request.decisionAt)}
                />
                <KeyValue
                  label="Cancelled / closed"
                  value={formatDateTime(request.cancelledAt)}
                />
                <KeyValue
                  label="Auto-expired"
                  value={formatDateTime(request.autoExpiredAt)}
                />
                <KeyValue
                  label="Attachment completed"
                  value={formatDateTime(request.attachmentCompletedAt)}
                />
              </div>

              {statusHistory.length > 0 ? (
                <div className="ocf-status-history ocf-status-timeline-list">
                  {statusHistory.map((item: any, index: number) => (
                    <div
                      className="ocf-status-history-item"
                      key={item.id || `${item.changedAt || "status"}-${index}`}
                    >
                      <div>
                        <strong>
                          Status updated
                          {item.toStatusName ? ` to ${item.toStatusName}` : ""}
                        </strong>
                        {item.note ? <p>{item.note}</p> : null}
                      </div>
                      <span>{formatDateTime(item.changedAt)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </details>

            {finalized ? (
              <p className="ocf-detail-finalized-note">
                This request is finalized and can’t be changed.
              </p>
            ) : null}
          </section>

          <aside className="ocf-detail-activity-column">
            <div className="ocf-detail-activity-inner">
              <p className="ocf-detail-activity-note">
                These entries are recorded by the current Clinic Admin user.
                Clinic Admin can document activity, complete attachment, or
                close post-invitation outcomes. Initial clinical decisions
                remain provider-controlled.
              </p>

              <PatientRequestActivityPanel
                requestId={request.requestId}
                currentStatus={normalizedStatus}
                actorRole="ClinicAdmin"
                initialActivities={activities}
                disabled={finalizeBusy}
                onActivityAdded={loadDetails}
              />
            </div>
          </aside>
        </div>
      )}

      {finalizePreview && pendingFinalizeAction ? (
        <div
          className="ocf-modal-backdrop ocf-decision-preview-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeFinalizePreview();
          }}
        >
          <div
            className="ocf-modal ocf-decision-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ocf-finalize-preview-title"
          >
            <div className="ocf-modal-header">
              <div>
                <h2 id="ocf-finalize-preview-title" className="ocf-modal-title">
                  Confirm email to patient
                </h2>
                <p className="ocf-modal-subtitle">
                  Review the exact email before you {" "}
                  {finalizeActionLabel(pendingFinalizeAction).toLowerCase()}.
                </p>
              </div>

              <button
                className="ocf-modal-close"
                type="button"
                onClick={closeFinalizePreview}
                disabled={!!submittingFinalize}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="ocf-email-preview-meta">
              <div>
                <span className="k">Action</span>
                <span className="v">
                  {finalizeActionLabel(pendingFinalizeAction)}
                </span>
              </div>
              <div>
                <span className="k">To</span>
                <span className="v">{finalizePreview.email?.to || "—"}</span>
              </div>
              <div>
                <span className="k">Subject</span>
                <span className="v">
                  {finalizePreview.email?.subject || "—"}
                </span>
              </div>
            </div>

            {!finalizePreview.email?.to ? (
              <div className="ocf-email-preview-warning">
                No patient/applicant email was found. The request can still be
                updated, but no email will be sent.
              </div>
            ) : null}

            <div className="ocf-email-preview-block">
              <div className="ocf-email-preview-label">Email body</div>
              <pre className="ocf-email-preview-body">
                {finalizePreview.email?.text || ""}
              </pre>
            </div>

            <div className="ocf-modal-actions">
              <button
                className="ocf-btn ocf-btn--secondary"
                type="button"
                onClick={closeFinalizePreview}
                disabled={!!submittingFinalize}
              >
                Cancel
              </button>

              <button
                className="ocf-btn ocf-btn--warn"
                type="button"
                onClick={onConfirmFinalizePreview}
                disabled={!!submittingFinalize}
              >
                {submittingFinalize
                  ? "Sending…"
                  : finalizeConfirmLabel(pendingFinalizeAction)}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}